/*
  URL → Summary tool. Paste a URL, get a 1-sentence TLDR + 3-5 key bullets.
*/
import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ExternalLink, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { phase3Api } from "../lib/phase3Api";

export default function UrlSummaryPage() {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);

  async function summarise(e) {
    e?.preventDefault?.();
    if (!url.trim() || busy) return;
    setBusy(true);
    setSummary(null);
    try {
      const res = await phase3Api.urlSummary(url.trim());
      setSummary(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't summarise — check the URL");
    } finally { setBusy(false); }
  }

  return (
    <div className="animate-fade-up max-w-2xl">
      <span className="label-caps">Quick tool</span>
      <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter">URL → Summary.</h1>
      <p className="mt-2 text-sm sm:text-base" style={{ color: "var(--text-muted)" }}>
        Paste an article URL — get a one-sentence TL;DR plus the key bullets.
      </p>

      <form onSubmit={summarise} className="card-tactile rounded-2xl p-5 mt-6 flex flex-col sm:flex-row gap-2">
        <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} required
          placeholder="https://…"
          data-testid="us-input"
          className="flex-1 px-3 py-2 rounded-md border bg-[color:var(--surface)] text-sm focus:outline-none focus:border-[color:var(--primary)]"
          style={{ borderColor: "var(--border-subtle)" }} />
        <button type="submit" disabled={busy}
          data-testid="us-submit"
          className="inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
          style={{ background: "var(--primary)" }}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Summarise
        </button>
      </form>

      {summary && (
        <div className="card-tactile rounded-2xl p-5 mt-5 space-y-4" data-testid="us-results">
          {summary.title && <div className="font-display font-bold text-lg tracking-tight">{summary.title}</div>}
          <div>
            <div className="label-caps mb-1 flex items-center gap-1"><FileText className="h-3 w-3" /> TL;DR</div>
            <p className="text-sm leading-relaxed">{summary.tldr}</p>
          </div>
          {summary.bullets?.length > 0 && (
            <div>
              <div className="label-caps mb-1">Key points</div>
              <ul className="space-y-1.5 list-disc pl-5">
                {summary.bullets.map((b, i) => <li key={i} className="text-sm">{b}</li>)}
              </ul>
            </div>
          )}
          <a href={summary.source_url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-semibold hover:underline"
            style={{ color: "var(--primary-ink)" }}>
            Read original <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}
    </div>
  );
}
